import React, { FC, ReactElement } from 'react'
import { Platform } from 'react-native'

import { useIntl } from 'react-intl'

import Stack, { StackProps } from 'app/components/base/Stack'
import Text, { TextProps } from 'app/components/base/Text'
import QuantityFieldNumber from 'app/components/fields/QuantityFieldNumber'
import { IS_NATIVE_APP } from 'app/utils/constants/env.constants'
import { moreThanQuantity } from 'app/utils/helpers/functions.helpers'

type QuantityControllerProps = {
  availableQuantity?: number
  soldQuantity?: number
  reservedQuantity?: number
  hideQuantity?: boolean
  isOffer?: boolean
  isSold?: boolean
  sx?: StackProps['sx']
}

const QuantityController: RFC<QuantityControllerProps> = ({
  availableQuantity,
  soldQuantity,
  reservedQuantity = 0,
  hideQuantity,
  isOffer,
  isSold,
  sx
}) => {
  const intl = useIntl()
  const { value: available } = moreThanQuantity(availableQuantity)
  const { value: reserved } = moreThanQuantity(reservedQuantity)
  const { value: sold } = moreThanQuantity(soldQuantity)

  if (hideQuantity) {
    return null
  }

  return (
    <Stack
      spacing={IS_NATIVE_APP ? 0 : 1.5}
      sx={{
        width: '100%',
        mt: { md: 0, xs: 1.5 },
        ...sx
      }}
    >
      {isSold ? (
        <Stack direction="row">
          <TextQuantity
            variant="h5"
            sx={{
              fontWeight: 500,
              color: 'error.main',
              mr: 0.5
            }}
            message={intl.formatMessage({
              defaultMessage: 'Out of Stock'
            })}
          />

          <TextQuantity
            message={intl.formatMessage(
              { defaultMessage: '| Sold: {sold}' },
              { sold }
            )}
          />
        </Stack>
      ) : (
        <>
          <Text variant="h5" component="h3" sx={{ fontWeight: 500 }}>
            {intl.formatMessage({
              defaultMessage: 'Quantity'
            })}
          </Text>

          <Stack
            direction="row"
            alignItems={IS_NATIVE_APP ? 'center' : 'flex-start'}
            spacing={2}
          >
            <QuantityFieldNumber
              name="item_quantity"
              disabled={!availableQuantity}
              maxValue={availableQuantity}
              formControlProps={{
                sx: {
                  ...(Platform.OS === 'web' && { width: 115 }),
                  ...(IS_NATIVE_APP && { width: 137, flex: 0 })
                }
              }}
            />

            <Stack sx={{ ...(IS_NATIVE_APP && { pt: 1 }) }}>
              <TextQuantity
                message={intl.formatMessage(
                  { defaultMessage: 'In stock: {available}' },
                  { available }
                )}
              />

              <Stack
                sx={{
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: { xs: 'flex-start', sm: 'center' },
                  ...(IS_NATIVE_APP && {
                    flexDirection: 'column',
                    alignItems: 'flex-start'
                  })
                }}
              >
                {isOffer && reserved > 0 && (
                  <>
                    <TextQuantity
                      message={intl.formatMessage(
                        { defaultMessage: 'Reserved: {reserved}' },
                        { reserved }
                      )}
                    />
                    <TextQuantity
                      message="|"
                      sx={{
                        mx: 0.5,
                        ...(Platform.OS === 'web' && {
                          display: { xs: 'none', sm: 'flex' }
                        })
                      }}
                    />
                  </>
                )}
                <TextQuantity
                  message={intl.formatMessage(
                    { defaultMessage: 'Sold: {sold}' },
                    { sold }
                  )}
                />
              </Stack>
            </Stack>
          </Stack>
        </>
      )}
    </Stack>
  )
}

type TextQuantityProps = {
  message: string | ReactElement
  sx?: TextProps['sx']
  variant?: TextProps['variant']
}

const TextQuantity: FC<TextQuantityProps> = ({ message, sx, ...rest }) => {
  return (
    <Text
      variant="h5"
      component="h4"
      sx={{ fontWeight: 500, color: 'primary.main', ...sx }}
      {...rest}
    >
      {message}
    </Text>
  )
}

export default QuantityController
